import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  FlaskConical, Search, Filter, Upload, CheckCircle2, ShieldCheck,
  Printer, X, Trash2, PlusCircle, Play, Download, ClipboardList,
  MessageCircle,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

/* ─────────────────────────── constants ─────────────────────────── */
const TYPES = ["All", "Lab", "X-Ray", "CT", "MRI", "USG", "ECG", "Echo", "Endoscopy", "EEG", "PFT", "Radiology", "Physiotherapy", "Other"];
const SOURCES = ["All", "opd", "ipd", "walkin", "manual"];
const STATUSES = [
  "All", "Pending", "In Process", "Report Pending",
  "Report Uploaded", "Verified", "Completed",
];
const STATUS_STYLE = {
  Pending:            "bg-slate-100 text-slate-700 border-slate-300",
  "In Process":       "bg-amber-50 text-amber-800 border-amber-300",
  "Report Pending":   "bg-orange-50 text-orange-800 border-orange-300",
  "Report Uploaded":  "bg-sky-50 text-sky-800 border-sky-300",
  Verified:           "bg-indigo-50 text-indigo-800 border-indigo-300",
  Completed:          "bg-teal-50 text-teal-800 border-teal-300",
};
const PAY_STYLE = {
  Paid:    "bg-teal-50 text-teal-800 border-teal-300",
  Partial: "bg-amber-50 text-amber-800 border-amber-300",
  Unpaid:  "bg-rose-50 text-rose-800 border-rose-300",
  "N/A":   "bg-slate-100 text-slate-600 border-slate-300",
};

const CAN_ENTER = ["admin", "super_admin", "doctor", "lab", "reception"];
const CAN_VERIFY = ["admin", "super_admin", "doctor", "lab"];

/* ─────────────────────────── main page ─────────────────────────── */
export default function Investigations() {
  const { user } = useAuth();
  const role = user?.role || "";
  const canEnter = CAN_ENTER.includes(role);
  const canVerify = CAN_VERIFY.includes(role);

  const [rows, setRows] = useState([]);
  // Filters are bundled into a single object so the `load` callback has one
  // stable dependency instead of six (keeps the hook-lint rule quiet and
  // avoids a re-run per state field).
  const [filters, setFilters] = useState({
    type: "All", status: "All", source: "All", search: "", from: "", to: "",
  });
  const setF = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null); // opened drawer id

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { type, status, source, search, from, to } = filters;
      const params = new URLSearchParams();
      if (type !== "All") params.set("type", type);
      if (status !== "All") params.set("status", status);
      if (source !== "All") params.set("source", source);
      if (search.trim()) params.set("search", search.trim());
      if (from) params.set("from_date", from);
      if (to) params.set("to_date", to);
      const r = await api.get(`/investigations?${params.toString()}`);
      setRows(r.data || []);
    } catch (e) {
      toast.error("Failed to load investigations");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { All: rows.length };
    TYPES.slice(1).forEach(t => { c[t] = rows.filter(r => (r.type || "Other") === t).length; });
    return c;
  }, [rows]);

  const statusCounts = useMemo(() => {
    const c = { All: rows.length };
    STATUSES.slice(1).forEach(s => { c[s] = rows.filter(r => r.status === s).length; });
    return c;
  }, [rows]);

  const { type, status, source, search, from, to } = filters;

  return (
    <div className="space-y-4" data-testid="investigations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-blue-700" />
            Investigations
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Central hub for all diagnostic orders — Lab, ECG, X-Ray, CT, MRI, USG &amp; more.
          </p>
        </div>
        <Button
          data-testid="inv-refresh"
          variant="outline"
          onClick={load}
          className="rounded-sm h-9"
        >
          Refresh
        </Button>
      </div>

      {/* Type tabs */}
      <div className="bg-white border rounded-sm">
        <div className="flex flex-wrap gap-1 p-2 border-b" data-testid="inv-type-tabs">
          {TYPES.map(t => (
            <button
              key={t}
              data-testid={`inv-tab-${t.toLowerCase().replace(/\s+/g,'-')}`}
              onClick={() => setF({ type: t })}
              className={`px-3 h-8 text-xs border rounded-sm transition-all ${
                type === t
                  ? "bg-blue-700 text-white border-blue-700 shadow-sm"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {t} <span className={`ml-1 ${type === t ? "opacity-80" : "text-slate-500"}`}>({counts[t] ?? 0})</span>
            </button>
          ))}
        </div>

        {/* Filters row */}
        <div className="p-3 grid grid-cols-1 md:grid-cols-7 gap-2 border-b">
          <div className="md:col-span-2 relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
            <Input
              data-testid="inv-search"
              placeholder="Patient / UHID / Test / Mobile / Doctor"
              value={search}
              onChange={(e) => setF({ search: e.target.value })}
              className="h-9 pl-7 rounded-sm"
            />
          </div>
          <div>
            <select
              data-testid="inv-status-filter"
              value={status}
              onChange={(e) => setF({ status: e.target.value })}
              className="w-full h-9 border rounded-sm text-sm px-2 bg-white"
            >
              {STATUSES.map(s => (
                <option key={s} value={s}>
                  {s}{s !== "All" && ` (${statusCounts[s] ?? 0})`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <select
              data-testid="inv-source-filter"
              value={source}
              onChange={(e) => setF({ source: e.target.value })}
              className="w-full h-9 border rounded-sm text-sm px-2 bg-white"
              title="Source"
            >
              {SOURCES.map(s => <option key={s} value={s}>{s === "All" ? "All sources" : s.toUpperCase()}</option>)}
            </select>
          </div>
          <Input
            data-testid="inv-from"
            type="date"
            value={from}
            onChange={(e) => setF({ from: e.target.value })}
            className="h-9 rounded-sm"
          />
          <Input
            data-testid="inv-to"
            type="date"
            value={to}
            onChange={(e) => setF({ to: e.target.value })}
            className="h-9 rounded-sm"
          />
          <Button
            data-testid="inv-clear"
            variant="outline"
            onClick={() => setF({ search: "", from: "", to: "", status: "All", type: "All", source: "All" })}
            className="h-9 rounded-sm"
          >
            <Filter className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        </div>

        {/* Rows */}
        <div className="overflow-auto">
          <table className="w-full text-xs" data-testid="inv-table">
            <thead className="bg-slate-50 border-b">
              <tr className="text-left">
                <th className="label-tiny p-2">Patient</th>
                <th className="label-tiny">UHID</th>
                <th className="label-tiny">Age·Sex</th>
                <th className="label-tiny">Mobile</th>
                <th className="label-tiny">OPD/IPD</th>
                <th className="label-tiny">Doctor · Dept</th>
                <th className="label-tiny">Type</th>
                <th className="label-tiny">Test</th>
                <th className="label-tiny">Ordered</th>
                <th className="label-tiny">Priority</th>
                <th className="label-tiny">Payment</th>
                <th className="label-tiny">Status</th>
                <th className="label-tiny text-right pr-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={13} className="p-6 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={13} className="p-6 text-center text-slate-500">
                  No investigation orders match the current filters.
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-b hover:bg-slate-50 cursor-pointer"
                  onClick={() => setActive(r.id)}
                  data-testid={`inv-row-${r.id}`}
                >
                  <td className="p-2 font-medium">{r.patient_name || "—"}</td>
                  <td className="font-mono text-[11px] text-slate-600">{r.uhid || "—"}</td>
                  <td>{r.age ?? "—"}{r.gender ? `·${(r.gender || "").slice(0,1)}` : ""}</td>
                  <td className="font-mono text-[11px]">{r.mobile || "—"}</td>
                  <td className="font-mono text-[11px]">
                    {r.opd_no || r.ipd_no || "—"}
                  </td>
                  <td className="text-slate-600">
                    {r.doctor_name || "—"}
                    {r.department ? <span className="text-slate-400"> · {r.department}</span> : null}
                  </td>
                  <td>
                    <Badge variant="outline" className="text-[10px]">{r.type || "Other"}</Badge>
                  </td>
                  <td className="font-medium">{r.name}</td>
                  <td className="text-[11px] text-slate-600">
                    {(r.ordered_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td>{r.priority || "Routine"}</td>
                  <td>
                    <span className={`text-[10px] px-1.5 py-0.5 border ${PAY_STYLE[r.payment_status] || PAY_STYLE["N/A"]}`}>
                      {r.payment_status || "N/A"}
                    </span>
                  </td>
                  <td>
                    <span className={`text-[10px] px-1.5 py-0.5 border ${STATUS_STYLE[r.status] || STATUS_STYLE.Pending}`}>
                      {r.status || "Pending"}
                    </span>
                  </td>
                  <td className="text-right pr-3">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); setActive(r.id); }}
                      className="h-7 text-xs"
                      data-testid={`inv-open-${r.id}`}
                    >
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {active && (
        <InvestigationDrawer
          id={active}
          canEnter={canEnter}
          canVerify={canVerify}
          onClose={() => setActive(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}

/* ─────────────────────── DRAWER ─────────────────────── */
function InvestigationDrawer({ id, canEnter, canVerify, onClose, onSaved }) {
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/investigations/${id}`);
      if (!mounted.current) return;   // drawer closed while request in-flight
      setDoc(r.data);
    } catch (e) {
      if (!mounted.current) return;
      toast.error("Failed to load"); onClose();
    }
  }, [id, onClose]);

  // Track mount state so async setState calls don't fire after unmount.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!doc) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex justify-end">
        <div className="w-full md:w-[880px] bg-white h-full flex items-center justify-center">
          Loading…
        </div>
      </div>
    );
  }

  const isLab = (doc.type || "").toLowerCase() === "lab";
  const isFinalized = doc.status === "Completed";
  const readOnly = isFinalized && !canVerify;

  const saveResults = async (payload, silent = false) => {
    setSaving(true);
    try {
      await api.post(`/investigations/${doc.id}/results`, payload);
      if (!silent) toast.success("Report saved");
      await load(); onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const advance = async (path, label) => {
    setSaving(true);
    try {
      await api.post(`/investigations/${doc.id}/${path}`);
      toast.success(label);
      await load(); onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setSaving(false); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB per file"); return; }
    const buf = await file.arrayBuffer();
    let bin = ""; const bytes = new Uint8Array(buf);
    for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
    const b64 = btoa(bin);
    setSaving(true);
    try {
      await api.post(`/investigations/${doc.id}/upload`, {
        filename: file.name, content_type: file.type || "application/octet-stream",
        data_base64: b64,
      });
      toast.success("File attached");
      await load(); onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setSaving(false); }
  };

  const removeAttachment = async (attId) => {
    if (!window.confirm("Remove this attachment?")) return;
    setSaving(true);
    try {
      await api.delete(`/investigations/${doc.id}/attachments/${attId}`);
      await load();
    } catch (e) { toast.error("Failed to remove"); }
    finally { setSaving(false); }
  };

  const downloadAtt = async (attId, filename) => {
    try {
      const r = await api.get(`/investigations/${doc.id}/attachments/${attId}`);
      const a = document.createElement("a");
      a.href = `data:${r.data.content_type};base64,${r.data.data_base64}`;
      a.download = filename || r.data.filename || "attachment";
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error("Download failed"); }
  };

  const printReport = () => {
    if (doc.public_token && (doc.type || "").toLowerCase() === "lab") {
      window.open(`/lab/report/${doc.public_token}?print=1`, "_blank");
      return;
    }
    // Build a same-origin Blob URL instead of document.write to avoid the
    // XSS-adjacent lint rule and keep an audit-safe path even though every
    // interpolation in reportHtml() is already run through escapeHtml.
    const html = reportHtml(doc);
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "print", "width=800,height=1000");
    if (!w) { URL.revokeObjectURL(url); toast.error("Popup blocked"); return; }
    const cleanup = () => setTimeout(() => URL.revokeObjectURL(url), 30_000);
    w.addEventListener("load", () => { try { w.print(); } finally { cleanup(); } }, { once: true });
    // Fallback in case the load event never fires (some browsers on Blob URLs)
    setTimeout(() => { try { w.print(); } catch {/* window closed */} cleanup(); }, 800);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" data-testid="inv-drawer">
      <div className="w-full md:w-[900px] bg-white h-full overflow-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between z-10">
          <div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px]">{doc.type || "Other"}</Badge>
              <h2 className="font-display text-lg font-semibold">{doc.name}</h2>
              <span className={`text-[10px] px-1.5 py-0.5 border ${STATUS_STYLE[doc.status] || ""}`}>
                {doc.status}
              </span>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Ordered {doc.ordered_at ? new Date(doc.ordered_at).toLocaleString() : "—"}
              {doc.ordered_by_name ? ` by ${doc.ordered_by_name}` : ""}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={printReport} data-testid="inv-print">
              <Printer className="w-3.5 h-3.5 mr-1" /> Print
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="inv-close">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Patient + order cards */}
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="border rounded-sm p-3 bg-slate-50">
            <div className="label-tiny mb-2">Patient</div>
            <div className="text-sm font-semibold">{doc.patient_name}</div>
            <div className="text-xs text-slate-600 font-mono">{doc.uhid}</div>
            <div className="text-xs mt-1">
              {doc.age ?? "—"} y · {doc.gender || "—"} · <span className="font-mono">{doc.mobile || "—"}</span>
            </div>
          </div>
          <div className="border rounded-sm p-3 bg-slate-50">
            <div className="label-tiny mb-2">Order</div>
            <div className="text-xs">Ref: <span className="font-mono">{doc.opd_no || doc.ipd_no || "—"}</span></div>
            <div className="text-xs">Doctor: {doc.doctor_name || "—"}</div>
            <div className="text-xs">Department: {doc.department || "—"}</div>
            <div className="text-xs">Priority: <b>{doc.priority || "Routine"}</b></div>
            <div className="text-xs mt-1">Payment:
              <span className={`ml-1 text-[10px] px-1.5 py-0.5 border ${PAY_STYLE[doc.payment_status] || PAY_STYLE["N/A"]}`}>
                {doc.payment_status || "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* QR / Public Report banner — appears once results are verified */}
        {doc.public_token && ["Verified", "Completed"].includes(doc.status) && (() => {
          const shareUrl = `${window.location.origin}/lab/report/${doc.public_token}`;
          const patientName = (doc.patient_name || "Patient").split(" ")[0];
          const hospitalName = user?.hospital?.name || "the hospital";
          const waText = `Hello ${patientName},\n\nYour ${doc.name} report is now ready. Please click the secure link below to view / download your report:\n\n${shareUrl}\n\nRegards,\n${hospitalName}`;
          // Normalise mobile (keep digits, add 91 India prefix if 10-digit)
          let raw = (doc.mobile || "").replace(/\D/g, "");
          if (raw.length === 10) raw = "91" + raw;
          const canWhatsApp = raw.length >= 10;
          const waUrl = canWhatsApp
            ? `https://wa.me/${raw}?text=${encodeURIComponent(waText)}`
            : `https://wa.me/?text=${encodeURIComponent(waText)}`;
          return (
            <div className="mx-5 mt-1 mb-3 border border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-sm p-3 flex items-center gap-3" data-testid="inv-qr-banner">
              <div className="bg-white p-1 border border-emerald-200 rounded-sm">
                <QRCodeSVG value={shareUrl} size={64} level="M" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-emerald-900 flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" /> Public Report Available
                </div>
                <div className="text-[10px] text-emerald-700 mt-0.5 break-all font-mono">
                  {shareUrl}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2">
                  {canWhatsApp ? (
                    <>Patient mobile: <b className="font-mono text-slate-700">+{raw}</b> — one-click WhatsApp share below.</>
                  ) : (
                    <span className="text-amber-700">⚠ No mobile on file — WhatsApp will open a contact picker instead.</span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1 min-w-[150px]">
                <Button size="sm"
                  data-testid="inv-whatsapp-share"
                  className="bg-[#25D366] hover:bg-[#1EBE5A] text-white font-semibold"
                  onClick={() => window.open(waUrl, "_blank", "noopener,noreferrer")}>
                  <MessageCircle className="w-3.5 h-3.5 mr-1" /> Share on WhatsApp
                </Button>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" data-testid="inv-copy-qr" className="flex-1"
                    onClick={() => {
                      navigator.clipboard.writeText(shareUrl);
                      toast.success("Link copied to clipboard");
                    }}>
                    Copy
                  </Button>
                  <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 flex-1" data-testid="inv-open-public"
                    onClick={() => window.open(`/lab/report/${doc.public_token}`, "_blank")}>
                    Open
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Clinical notes */}
        {doc.clinical_notes && (
          <div className="px-5 pb-3">
            <div className="label-tiny mb-1">Clinical Notes</div>
            <div className="text-xs bg-yellow-50 border-yellow-200 border p-2 rounded-sm whitespace-pre-wrap">
              {doc.clinical_notes}
            </div>
          </div>
        )}

        {/* Type-specific body */}
        <div className="px-5 pb-5 space-y-4">
          {isLab ? (
            <LabResultsEditor doc={doc} onSave={saveResults} readOnly={readOnly || !canEnter} />
          ) : (
            <ImagingFindingsEditor doc={doc} onSave={saveResults} readOnly={readOnly || !canEnter} />
          )}

          {/* Attachments */}
          <div className="border rounded-sm p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="label-tiny flex items-center gap-1">
                <Upload className="w-3 h-3" /> Attachments ({(doc.attachments || []).length})
              </div>
              {canEnter && !isFinalized && (
                <>
                  <input
                    ref={fileRef}
                    type="file"
                    className="hidden"
                    onChange={(e) => uploadFile(e.target.files[0])}
                    accept="image/*,application/pdf"
                    data-testid="inv-file-input"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    data-testid="inv-upload-btn"
                  >
                    <Upload className="w-3.5 h-3.5 mr-1" /> Upload File
                  </Button>
                </>
              )}
            </div>
            <div className="space-y-1">
              {(doc.attachments || []).length === 0 && (
                <div className="text-[11px] text-slate-500">No files attached yet.</div>
              )}
              {(doc.attachments || []).map((a) => (
                <div key={a.id} className="flex items-center justify-between border rounded-sm px-2 py-1 text-xs">
                  <div className="flex items-center gap-2 truncate">
                    <span className="font-mono truncate">{a.filename}</span>
                    <span className="text-[10px] text-slate-500">
                      {(a.content_type || "").split("/")[1] || "file"}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <button
                      className="text-blue-700 hover:text-blue-900"
                      onClick={() => downloadAtt(a.id, a.filename)}
                      data-testid={`inv-att-dl-${a.id}`}
                    ><Download className="w-3.5 h-3.5" /></button>
                    {canEnter && !isFinalized && (
                      <button
                        className="text-rose-700 hover:text-rose-900"
                        onClick={() => removeAttachment(a.id)}
                      ><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Audit trail */}
          <details className="border rounded-sm p-3">
            <summary className="cursor-pointer text-xs font-medium flex items-center gap-1">
              <ClipboardList className="w-3.5 h-3.5" /> Audit trail ({(doc.audit_trail || []).length})
            </summary>
            <div className="mt-2 space-y-1">
              {(doc.audit_trail || []).map((a, idx) => (
                <div key={`${a.at || ""}-${a.action || ""}-${idx}`} className="text-[11px] flex gap-2 border-b py-1">
                  <span className="font-mono text-slate-500 whitespace-nowrap">
                    {(a.at || "").slice(0, 19).replace("T", " ")}
                  </span>
                  <span className="uppercase font-semibold text-[10px] text-blue-700">
                    {a.action}
                  </span>
                  <span className="text-slate-700">
                    {a.user_name || a.user_id} <span className="text-slate-400">({a.role})</span>
                  </span>
                </div>
              ))}
            </div>
          </details>
        </div>

        {/* Sticky footer actions */}
        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex flex-wrap gap-2 justify-end">
          {canEnter && doc.status === "Pending" && (
            <Button
              variant="outline"
              onClick={() => advance("start", "Marked In Process")}
              disabled={saving}
              data-testid="inv-start"
            >
              <Play className="w-3.5 h-3.5 mr-1" /> Start / In Process
            </Button>
          )}
          {canVerify && doc.status === "Report Uploaded" && (
            <Button
              onClick={() => advance("verify", "Report verified")}
              disabled={saving}
              className="bg-indigo-700 hover:bg-indigo-800"
              data-testid="inv-verify"
            >
              <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Verify
            </Button>
          )}
          {canVerify && (doc.status === "Verified" || doc.status === "Report Uploaded") && (
            <Button
              onClick={() => advance("finalize", "Report finalized")}
              disabled={saving}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="inv-finalize"
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Finalize
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── LAB EDITOR ─────────────────────── */
function LabResultsEditor({ doc, onSave, readOnly }) {
  // Assign each row a stable client-side id so React reconciliation stays
  // correct when rows are added/deleted (index keys would swap DOM state).
  const idRef = useRef(0);
  const withUid = (r) => ({ __uid: `lr-${++idRef.current}`, ...r });
  const [rows, setRows] = useState(() =>
    Array.isArray(doc.lab_results) && doc.lab_results.length
      ? doc.lab_results.map(withUid)
      : [withUid({ test: doc.name, result: "", unit: "", ref_range: "", flag: "" })]
  );
  const [remarks, setRemarks] = useState(doc.remarks || "");
  const [tech, setTech] = useState(doc.technician || "");
  const [tmplLoaded, setTmplLoaded] = useState(false);

  // Try to auto-populate parameter rows from the lab template once per drawer
  // open — but only if the current rows are empty / default scaffolding.
  useEffect(() => {
    const alreadyFilled = rows.some(r => (r.result || "").trim());
    if (alreadyFilled || tmplLoaded) return;
    (async () => {
      try {
        // find template by lab_template_id OR by test name
        let tmpl = null;
        if (doc.lab_template_id) {
          const r = await api.get(`/lab-templates/${doc.lab_template_id}`);
          tmpl = r.data;
        } else {
          const r = await api.get("/lab-templates", { params: { q: doc.name } });
          tmpl = (r.data || []).find(t => (t.name || "").toUpperCase() === (doc.name || "").toUpperCase());
        }
        if (!tmpl || !(tmpl.parameters || []).length) return;
        const sex = (doc.gender || "").toLowerCase();
        const refOf = (p) => (sex.startsWith("m") && p.ref_range_male) ? p.ref_range_male
          : (sex.startsWith("f") && p.ref_range_female) ? p.ref_range_female
          : (p.ref_range || "");
        setRows(tmpl.parameters.map(p => withUid({
          test: p.name, result: "", unit: p.unit || "", ref_range: refOf(p),
          flag: "", group: p.group || "",
        })));
        setTmplLoaded(true);
        toast.success(`Loaded ${tmpl.parameters.length} parameters from ${tmpl.name}`);
      } catch (e) { /* template optional */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setRow = (i, k, v) => {
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  };
  const addRow = () => setRows(prev => [...prev, withUid({ test: "", result: "", unit: "", ref_range: "", flag: "" })]);
  const delRow = (i) => setRows(prev => prev.filter((_, idx) => idx !== i));

  return (
    <div className="border rounded-sm p-3" data-testid="inv-lab-editor">
      <div className="flex items-center justify-between mb-2">
        <div className="label-tiny">Lab Results ({rows.length} row{rows.length !== 1 ? "s" : ""})</div>
        {!readOnly && (
          <Button size="sm" variant="outline" onClick={addRow} data-testid="lab-add-row">
            <PlusCircle className="w-3.5 h-3.5 mr-1" /> Add Test
          </Button>
        )}
      </div>
      <div className="overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b">
            <tr className="text-left">
              <th className="label-tiny p-2 w-64">Test</th>
              <th className="label-tiny">Result</th>
              <th className="label-tiny">Unit</th>
              <th className="label-tiny">Ref. Range</th>
              <th className="label-tiny">Flag</th>
              {!readOnly && <th></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.__uid || i} className="border-b">
                <td className="p-1">
                  <Input value={r.test} onChange={(e) => setRow(i, "test", e.target.value)} disabled={readOnly} className="h-8" data-testid={`lab-test-${i}`} />
                </td>
                <td className="p-1">
                  <Input value={r.result} onChange={(e) => setRow(i, "result", e.target.value)} disabled={readOnly} className="h-8" data-testid={`lab-result-${i}`} />
                </td>
                <td className="p-1">
                  <Input value={r.unit} onChange={(e) => setRow(i, "unit", e.target.value)} disabled={readOnly} className="h-8 w-24" />
                </td>
                <td className="p-1">
                  <Input value={r.ref_range} onChange={(e) => setRow(i, "ref_range", e.target.value)} disabled={readOnly} className="h-8 w-32" />
                </td>
                <td className="p-1">
                  <select
                    value={r.flag || ""}
                    onChange={(e) => setRow(i, "flag", e.target.value)}
                    disabled={readOnly}
                    className="h-8 border rounded-sm text-xs px-1"
                  >
                    <option value="">Normal</option>
                    <option value="High">High</option>
                    <option value="Low">Low</option>
                    <option value="Abnormal">Abnormal</option>
                    <option value="Critical">Critical</option>
                  </select>
                </td>
                {!readOnly && (
                  <td className="p-1">
                    <button onClick={() => delRow(i)} className="text-rose-700 hover:text-rose-900">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3">
        <div>
          <div className="label-tiny mb-1">Technician</div>
          <Input value={tech} onChange={(e) => setTech(e.target.value)} disabled={readOnly} className="h-8" />
        </div>
        <div>
          <div className="label-tiny mb-1">Remarks</div>
          <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={readOnly} className="h-8" />
        </div>
      </div>

      {!readOnly && (
        <div className="mt-3 flex justify-end">
          <Button
            onClick={() => onSave({
              lab_results: rows.map(({ __uid, ...rest }) => rest),  // strip client-only id
              remarks, technician: tech,
            })}
            className="bg-blue-700 hover:bg-blue-800"
            data-testid="lab-save"
          >
            Save Lab Report
          </Button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── IMAGING EDITOR ─────────────────────── */
function ImagingFindingsEditor({ doc, onSave, readOnly }) {
  const [findings, setFindings] = useState(doc.findings || "");
  const [impression, setImpression] = useState(doc.impression || "");
  const [remarks, setRemarks] = useState(doc.remarks || "");
  const [tech, setTech] = useState(doc.technician || "");

  return (
    <div className="border rounded-sm p-3 space-y-2" data-testid="inv-imaging-editor">
      <div className="label-tiny">Report</div>
      <div>
        <div className="label-tiny mb-1">Findings</div>
        <textarea
          value={findings}
          onChange={(e) => setFindings(e.target.value)}
          disabled={readOnly}
          rows={5}
          className="w-full border rounded-sm text-sm p-2 font-mono"
          placeholder="Detailed findings…"
          data-testid="img-findings"
        />
      </div>
      <div>
        <div className="label-tiny mb-1">Impression</div>
        <textarea
          value={impression}
          onChange={(e) => setImpression(e.target.value)}
          disabled={readOnly}
          rows={3}
          className="w-full border rounded-sm text-sm p-2"
          placeholder="Clinical impression / conclusion"
          data-testid="img-impression"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="label-tiny mb-1">Technician / Radiologist</div>
          <Input value={tech} onChange={(e) => setTech(e.target.value)} disabled={readOnly} className="h-8" />
        </div>
        <div>
          <div className="label-tiny mb-1">Remarks</div>
          <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={readOnly} className="h-8" />
        </div>
      </div>
      {!readOnly && (
        <div className="flex justify-end pt-1">
          <Button
            onClick={() => onSave({ findings, impression, remarks, technician: tech })}
            className="bg-blue-700 hover:bg-blue-800"
            data-testid="img-save"
          >
            Save Report
          </Button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── Report HTML for Print ─────────────────────── */
function reportHtml(d) {
  const labRows = (d.lab_results || []).map(r => `
    <tr>
      <td>${escapeHtml(r.test || "")}</td>
      <td>${escapeHtml(r.result || "")}</td>
      <td>${escapeHtml(r.unit || "")}</td>
      <td>${escapeHtml(r.ref_range || "")}</td>
      <td>${escapeHtml(r.flag || "")}</td>
    </tr>`).join("");
  const isLab = (d.type || "").toLowerCase() === "lab";
  return `
<!doctype html>
<html><head><meta charset="utf-8" /><title>${escapeHtml(d.name)} — ${escapeHtml(d.patient_name)}</title>
<style>
  body{font-family:Arial, Helvetica, sans-serif; padding:24px; color:#111}
  h1{font-size:18px; margin:0 0 4px}
  .grid{display:grid; grid-template-columns:1fr 1fr; gap:8px; margin:12px 0}
  .card{border:1px solid #cbd5e1; padding:8px; border-radius:2px}
  table{width:100%; border-collapse:collapse; margin-top:8px}
  th,td{border:1px solid #cbd5e1; padding:6px 8px; text-align:left; font-size:12px}
  th{background:#f1f5f9}
  .muted{color:#64748b; font-size:11px}
  .sig{margin-top:36px; display:flex; justify-content:space-between}
  .sig div{border-top:1px solid #cbd5e1; padding-top:6px; width:200px; text-align:center; font-size:11px}
  pre{white-space:pre-wrap; font-family:inherit; font-size:12px}
</style></head><body>
<h1>${escapeHtml(d.name)}</h1>
<div class="muted">${escapeHtml(d.type || "")} · Ordered ${escapeHtml((d.ordered_at || "").slice(0,19).replace("T"," "))}</div>
<div class="grid">
  <div class="card">
    <b>${escapeHtml(d.patient_name)}</b><br/>
    UHID: ${escapeHtml(d.uhid || "")} · ${d.age ?? ""}y · ${escapeHtml(d.gender || "")}<br/>
    Mobile: ${escapeHtml(d.mobile || "")}
  </div>
  <div class="card">
    Ref: ${escapeHtml(d.opd_no || d.ipd_no || "")}<br/>
    Doctor: ${escapeHtml(d.doctor_name || "")}<br/>
    Department: ${escapeHtml(d.department || "")}<br/>
    Priority: ${escapeHtml(d.priority || "Routine")}
  </div>
</div>
${d.clinical_notes ? `<div class="card"><b>Clinical Notes</b><br/><pre>${escapeHtml(d.clinical_notes)}</pre></div>` : ""}
${isLab ? `
<table><thead><tr><th>Test</th><th>Result</th><th>Unit</th><th>Ref. Range</th><th>Flag</th></tr></thead>
<tbody>${labRows || `<tr><td colspan="5" class="muted">No results entered.</td></tr>`}</tbody></table>` : `
<div class="card" style="margin-top:8px"><b>Findings</b><br/><pre>${escapeHtml(d.findings || "-")}</pre></div>
<div class="card" style="margin-top:8px"><b>Impression</b><br/><pre>${escapeHtml(d.impression || "-")}</pre></div>`}
${d.remarks ? `<div class="card" style="margin-top:8px"><b>Remarks</b><br/><pre>${escapeHtml(d.remarks)}</pre></div>` : ""}
<div class="sig">
  <div>${escapeHtml(d.technician || d.report_entered_by_name || "Technician")}</div>
  <div>${escapeHtml(d.verified_by_name || d.finalized_by_name || "Verified By")}</div>
</div>
<div style="margin-top:16px;padding-top:4px;text-align:center;font-size:8px;letter-spacing:0.14em;text-transform:uppercase;color:#94a3b8;page-break-inside:avoid;break-inside:avoid;">
  Powered by <span style="font-weight:600;color:#64748b;">The&nbsp;Vyomra&nbsp;HOS</span>
</div>
</body></html>`;
}

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, m => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[m]));
}
